import React, { useState } from 'react';
import { isDate, parse } from 'date-fns';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { IArchivingInfo, ISchool, SchoolParser } from './models';
import { SchoolApi } from 'api/schoolApi';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { UpdateArchiveDateForm } from './UpdateArchiveDateForm';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import ArchiveInstantly from './ArchiveInstantly';

const validationSchemaArchivingDate = Yup.object({
  password: Yup.string().required('A password is required'),
  yearlyArchivingDate: Yup.date()
    .required('An archiving date is required')
    .typeError('Date of birth is invalid. Format is dd/mm/yyyy')
    .transform(parseDateString),
});

function parseDateString(value: any, originalValue: any) {
  const parsedDate = isDate(originalValue)
    ? originalValue
    : parse(originalValue, 'dd/MM/yyyy', new Date());
  return parsedDate;
}

export type Options = 'archiveNow' | 'updateDate';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSchoolDetails: React.Dispatch<React.SetStateAction<ISchool | null>>;
};

export const UpdateArchive: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  setSchoolDetails,
}) => {
  const [error, setError] = useState<string>('');

  const [openArchivePopup, setOpenArchivePopup] = useState<boolean>(false);
  const [archiveNowError, setArchiveNowError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmitArchivingDate = async (
    values: IArchivingInfo,
    { setSubmitting }: FormikHelpers<IArchivingInfo>
  ) => {
    setSubmitting(true);
    const result = await SchoolApi.submitArchiveDate(values);
    if (result.data) {
      setSchoolDetails(result.data);
      togglePanelOpen(false);
      setSubmitting(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setSubmitting(false);
    }
  };

  const submitArchiveNow = async (
    setSchoolDetails: React.Dispatch<React.SetStateAction<ISchool | null>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
    values: any,
    setLoading: (isSubmitting: boolean) => void,
    closePanel: () => void
  ) => {
    setLoading(true);
    const result = await SchoolApi.submitArchiveNow(values);
    if (result.data) {
      setSchoolDetails(result.data);
      setLoading(false);
      setOpenArchivePopup(false);
      closePanel();
      document.body.style.overflowY = '';
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setLoading(false);
    }
  };
  
  const handleArchiveNow = (values: any) => {
    submitArchiveNow(
      setSchoolDetails,
      setArchiveNowError,
      values,
      setLoading,
      () => togglePanelOpen(false)
    );
  };  

  return (
    <Formik
      initialValues={SchoolParser.createEmptyArchivingInfo()}
      onSubmit={(values, formikHelpers) =>
        handleSubmitArchivingDate(values, formikHelpers)
      }
      validationSchema={validationSchemaArchivingDate}
      validateOnBlur={false}
    >
      {({ values, isSubmitting }) => (
        <>
          {openArchivePopup && (
            <ArchiveInstantly
              panelOpen={openArchivePopup}
              togglePanelOpen={setOpenArchivePopup}
              archive={() => handleArchiveNow(values)}
              error={archiveNowError}
            />
          )} 

          <PopupOrPanel
            isPopup={false}
            isOpen={panelOpen}
            isSubmitting={isSubmitting}
            position="right"
            hdrTitle="Archive Now"
            primaryBtnTxt={`${'Archive Now'}`}
            secondaryBtnTxt="Cancel"
            givenClass="ManageSchoolPopoutMenu"
            handleClose={() => togglePanelOpen(false)}
            handlePrimaryBtn={
              () => {
                setOpenArchivePopup(true);
              }
            }
            handleSecondaryBtn={() => {
              togglePanelOpen(false);
            }}
            width={560}
          >
            <FormGeneralError message={error} />
            <UpdateArchiveDateForm />
          </PopupOrPanel>         
        </>
      )}
    </Formik>
  );
};
